import React, { memo, useRef, useEffect } from 'react';
import { TrendingUp, ChevronRight } from 'lucide-react';
import StandaloneInput from './StandaloneInput';

const InputForm = ({ 
  userInputs, 
  handleInputChange, 
  handlePredict, 
  loading, 
  filterOptions, 
  maxFee 
}) => {
  const quotaRef = useRef(null);
  const categoryRef = useRef(null);
  const coursesRef = useRef(null);

  // Set initial values for select elements
  useEffect(() => {
    if (quotaRef.current && quotaRef.current.value !== userInputs.quota) {
      quotaRef.current.value = userInputs.quota || '';
    }
    if (categoryRef.current && categoryRef.current.value !== userInputs.category) {
      categoryRef.current.value = userInputs.category || '';
    }
  }, [userInputs.quota, userInputs.category]);

  const handleRankChange = (e) => {
    handleInputChange('rank', e.target.value);
  };

  const handleBudgetChange = (e) => {
    handleInputChange('budget', e.target.value);
  };

  const handleQuotaChange = (e) => {
    handleInputChange('quota', e.target.value);
  };

  const handleCategoryChange = (e) => {
    handleInputChange('category', e.target.value);
  };

  const handleCoursesChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleInputChange('preferredCourses', selectedOptions);
  };

  const handleBudgetPreset = (value) => {
    handleInputChange('budget', value);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Enter Your Details</h3>
          <p className="text-gray-600">Provide your information to get accurate predictions</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                NEET PG Rank *
              </label>
              <StandaloneInput
                type="number"
                value={userInputs.rank}
                onChange={handleRankChange}
                placeholder="Enter your NEET PG rank"
                className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Quota *
              </label>
              <select
                ref={quotaRef}
                value={userInputs.quota}
                onChange={handleQuotaChange}
                className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200"
              >
                <option value="">Select Quota</option>
                {filterOptions.quotas && filterOptions.quotas.map(q => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Category *
              </label>
              <select
                ref={categoryRef}
                value={userInputs.category}
                onChange={handleCategoryChange}
                className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200"
              >
                <option value="">Select Category</option>
                {filterOptions.categories && filterOptions.categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Preferred Courses (Optional)
              </label>
              <select
                ref={coursesRef}
                multiple
                value={userInputs.preferredCourses || []}
                onChange={handleCoursesChange}
                className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200 min-h-[120px]"
              >
                {filterOptions.courses && filterOptions.courses.length > 0 ? (
                  filterOptions.courses.map(course => (
                    <option key={course} value={course} className="py-2">
                      {course}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No courses available</option>
                )}
              </select>
              <p className="text-xs text-gray-500 mt-2">Hold Ctrl/Cmd to select multiple courses</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Budget Limit (Optional)
              </label>
              
              {/* Quick Budget Presets */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Quick Presets:</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('50000')}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                  >
                    ₹50K
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('100000')}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                  >
                    ₹1L
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('200000')}
                    className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
                  >
                    ₹2L
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('500000')}
                    className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                  >
                    ₹5L
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('1000000')}
                    className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200 transition-colors"
                  >
                    ₹10L
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetPreset('')}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    No Limit
                  </button>
                </div>
              </div>
              
              <div className="relative">
                <StandaloneInput
                  type="number"
                  value={userInputs.budget}
                  onChange={handleBudgetChange}
                  placeholder={`Enter maximum fee (up to ₹${maxFee?.toLocaleString('en-IN') || '2,000,000'})`}
                  max={maxFee}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200"
                />
                <div className="absolute right-4 top-4 text-gray-400 text-lg">₹</div>
              </div>
              <div className="text-xs text-gray-500 mt-1">Maximum fee in your data: ₹{maxFee?.toLocaleString('en-IN') || '2,000,000'}</div>
            </div>
          </div>
        </div>
        
        <div className="mt-10 text-center">
          <button
            onClick={handlePredict}
            disabled={!userInputs.rank || !userInputs.quota || !userInputs.category || loading}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-5 px-16 rounded-2xl font-bold text-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] shadow-xl disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-white mr-3"></div>
                Analyzing Your Options...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <TrendingUp className="h-7 w-7 mr-3" />
                Get My Personalized Predictions
                <ChevronRight className="h-7 w-7 ml-3" />
              </div>
            )}
          </button>
          
          {(!userInputs.rank || !userInputs.quota || !userInputs.category) && (
            <p className="text-red-500 mt-4 font-medium">
              Please fill in all required fields (*) to continue
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

InputForm.displayName = 'InputForm';

// Custom comparison function to prevent unnecessary re-renders
const areEqual = (prevProps, nextProps) => {
  // Only re-render if userInputs or loading actually changed
  return (
    prevProps.userInputs.rank === nextProps.userInputs.rank &&
    prevProps.userInputs.quota === nextProps.userInputs.quota &&
    prevProps.userInputs.category === nextProps.userInputs.category &&
    prevProps.userInputs.budget === nextProps.userInputs.budget &&
    JSON.stringify(prevProps.userInputs.preferredCourses) === JSON.stringify(nextProps.userInputs.preferredCourses) &&
    prevProps.loading === nextProps.loading &&
    prevProps.filterOptions === nextProps.filterOptions
  );
};

export default memo(InputForm, areEqual); 